/**
 * Light/dark theme — the app counterpart of the prism-landing toggle.
 * Light is the default; the `.light` class on <html> flips the token ramp
 * defined in app/styles/index.css (whose `:root` values are the dark ramp).
 * Preference persists in localStorage and is applied before first paint
 * (see main.tsx).
 */

const KEY = 'og-theme';

export type Theme = 'light' | 'dark';

export function getTheme(): Theme {
  // Light is the default; users opt into dark explicitly.
  try {
    return localStorage.getItem(KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.classList.toggle('light', theme === 'light');
  // index.html hardcodes class="dark" for a dark-toned pre-paint (matching the
  // :root token values). It must come OFF in light mode, or every Tailwind
  // `dark:` variant keeps matching underneath the light token overrides.
  root.classList.toggle('dark', theme === 'dark');
}

export function setTheme(theme: Theme) {
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    /* private mode — session-only */
  }
  applyTheme(theme);
}

export function toggleTheme(): Theme {
  const next: Theme = getTheme() === 'light' ? 'dark' : 'light';
  setTheme(next);
  return next;
}

/** Call once at boot, before React renders, to avoid a theme flash. */
export function initTheme() {
  applyTheme(getTheme());
}
